export function faqWords(value) {
  return String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().split(/\s+/).filter(Boolean);
}
const announceLibraries = () => queueMicrotask(() => document.dispatchEvent(new CustomEvent('bm-faq:ready')));
const scrollToFaq = element => {
  element.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
};

class FaqLibrary extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.query = '';
    this.savedOpen = null;
    this.topics = [...this.querySelectorAll('[data-faq-topic]')];
    this.questions = [...this.querySelectorAll('[data-faq-question]')];
    this.text = new Map(this.questions.map(question => [question, faqWords(`${question.querySelector('[data-faq-question-text]').textContent} ${question.querySelector('[data-faq-answer]').textContent} ${question.dataset.faqKeywords || ''}`).join(' ')]));
    this.nav = this.querySelector('[data-faq-nav]');
    this.results = this.querySelector('.bm-faq__results');
    this.status = this.querySelector('[data-faq-status]');
    this.empty = this.querySelector('[data-faq-no-results]');
    const list = this.nav.querySelector('ul');
    list.replaceChildren();
    this.links = new Map();
    this.topics.forEach(topic => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${topic.id}`;
      link.textContent = topic.querySelector('h2').textContent;
      link.addEventListener('click', event => {
        event.preventDefault();
        history.replaceState(null, '', link.hash);
        topic.querySelector('h2').focus({ preventScroll: true });
        scrollToFaq(topic);
        this.markCurrent(topic);
      }, { signal });
      item.append(link);list.append(item);this.links.set(topic, link);
    });
    this.nav.hidden = !this.topics.length;
    this.addEventListener('shopify:block:select', event => {
      const question = event.target.closest('[data-faq-question]');
      const topic = event.target.closest('[data-faq-topic]');
      if (question || topic) {
        this.filter('');
        if (question) question.open = true;
        if (topic) this.markCurrent(topic);
      }
    }, { signal });
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const visible = this.topics.filter(topic => !topic.hidden);
        const current = visible.filter(topic => topic.getBoundingClientRect().top <= 160).at(-1) || visible[0];
        this.markCurrent(current);
      });
    };
    window.addEventListener('scroll', this.onScroll, { signal, passive: true });
    window.addEventListener('resize', this.onScroll, { signal, passive: true });
    window.addEventListener('hashchange', () => this.revealHash(), { signal });
    this.filter('');
    this.revealHash();
    announceLibraries();
  }

  markCurrent(topic) {
    this.links.forEach((link, item) => {
      if (item === topic) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }

  filter(value) {
    const words = faqWords(value);
    if (words.length && !this.savedOpen) this.savedOpen = new Map(this.questions.map(question => [question, question.open]));
    let count = 0;
    this.questions.forEach(question => {
      const matches = words.every(word => this.text.get(question).includes(word));
      question.hidden = !matches;
      if (matches) count++;
      if (words.length && matches) question.open = true;
      else if (!words.length && this.savedOpen) question.open = this.savedOpen.get(question);
    });
    if (!words.length) this.savedOpen = null;
    this.topics.forEach(topic => {
      topic.hidden = ![...topic.querySelectorAll('[data-faq-question]')].some(question => !question.hidden);
      this.links.get(topic).parentElement.hidden = topic.hidden;
    });
    this.nav.hidden = !this.topics.some(topic => !topic.hidden);
    this.status.textContent = words.length ? (this.dataset.resultsLabel || '').replaceAll('[count]', String(count)) : '';
    this.empty.hidden = !words.length || count > 0;
    this.query = value;
    this.onScroll();
    document.dispatchEvent(new CustomEvent('bm-faq:query', { detail: { library: this, value } }));
  }

  revealHash() {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    const target = document.getElementById(id);
    if (!target || !this.contains(target)) return;
    const question = target.closest('[data-faq-question]');
    const topic = target.closest('[data-faq-topic]');
    if (!question && !topic) return;
    this.filter('');
    if (question) question.open = true;
    (question?.querySelector('summary') || topic.querySelector('h2')).focus({ preventScroll: true });
    scrollToFaq(target);
  }

  disconnectedCallback() {
    this.abort?.abort();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    announceLibraries();
  }
}

class FaqSearch extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.input = this.querySelector('input');
    this.clear = this.querySelector('[data-faq-clear]');
    this.refresh = () => {
      const library = document.querySelector('bm-faq-library');
      this.library = library?.questions ? library : null;
      this.hidden = !this.library || !this.library.questions.length;
      if (this.library) {
        this.input.setAttribute('aria-controls', this.library.results.id);
        this.library.filter(this.input.value);
      }
    };
    this.input.addEventListener('input', () => this.library?.filter(this.input.value), { signal });
    this.clear.addEventListener('click', () => {
      this.input.value = '';
      this.library?.filter('');
      this.input.focus();
    }, { signal });
    this.querySelector('form').addEventListener('submit', event => {
      event.preventDefault();
      if (!this.library) return;
      this.library.filter(this.input.value);
      this.library.results.focus({ preventScroll: true });
      scrollToFaq(this.library.results);
    }, { signal });
    document.addEventListener('bm-faq:ready', this.refresh, { signal });
    document.addEventListener('bm-faq:query', event => {
      if (event.detail.library !== this.library) return;
      this.input.value = event.detail.value;
      this.clear.hidden = !faqWords(event.detail.value).length;
    }, { signal });
    this.refresh();
  }
  disconnectedCallback() { this.abort?.abort(); }
}
if (!customElements.get('bm-faq-library')) customElements.define('bm-faq-library', FaqLibrary);
if (!customElements.get('bm-faq-search')) customElements.define('bm-faq-search', FaqSearch);
